import './style/global.less';
import React, { useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { createStore } from 'redux';
import { Provider } from 'react-redux';
import { ConfigProvider } from '@arco-design/web-react';
import zhCN from '@arco-design/web-react/es/locale/zh-CN';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import { generate, getRgbStr } from '@arco-design/color';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import rootReducer from './store';
import PageLayout from './layout';
import { GlobalContext } from './context';
import { ProjectPermissionsProvider } from './contexts/ProjectPermissionsContext';
import Login from './pages/login';
import checkLogin from './utils/checkLogin';
import changeTheme from './utils/changeTheme';
import useStorage from './utils/useStorage';
import defaultSettings from './settings.json';
import apiClient from './api/config';
import { getUserPermissions } from './api/permissions';
import { createLogger } from './utils/logger';
import './mock';

const logger = createLogger('App');

// 抑制 ResizeObserver 循环警告（这是浏览器已知问题，不影响功能）
const originalError = window.onerror;
const originalConsoleError = console.error;

// 重写 window.onerror
window.onerror = (message, source, lineno, colno, error) => {
  const messageStr = String(message || '');
  if (
    messageStr.includes('ResizeObserver loop') ||
    messageStr.includes('ResizeObserver loop limit exceeded') ||
    messageStr.includes(
      'ResizeObserver loop completed with undelivered notifications'
    )
  ) {
    return true; // 抑制此错误
  }
  if (originalError) {
    return originalError(message, source, lineno, colno, error);
  }
  return false;
};

// 重写 console.error 以捕获 ResizeObserver 错误
console.error = (...args) => {
  const errorMessage = args.join(' ');
  if (
    errorMessage.includes('ResizeObserver loop') ||
    errorMessage.includes('ResizeObserver loop limit exceeded') ||
    errorMessage.includes(
      'ResizeObserver loop completed with undelivered notifications'
    )
  ) {
    return; // 不输出 ResizeObserver 相关错误
  }
  originalConsoleError.apply(console, args);
};

// 捕获未处理的错误事件
window.addEventListener(
  'error',
  (e) => {
    const errorMessage = e.message || String(e.error || '');
    if (
      errorMessage.includes('ResizeObserver loop') ||
      errorMessage.includes('ResizeObserver loop limit exceeded') ||
      errorMessage.includes(
        'ResizeObserver loop completed with undelivered notifications'
      )
    ) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  },
  true
); // 使用捕获阶段

// 捕获未处理的 Promise 拒绝
window.addEventListener('unhandledrejection', (e) => {
  const errorMessage = e.reason?.message || String(e.reason || '');
  if (
    errorMessage.includes('ResizeObserver loop') ||
    errorMessage.includes('ResizeObserver loop limit exceeded') ||
    errorMessage.includes(
      'ResizeObserver loop completed with undelivered notifications'
    )
  ) {
    e.preventDefault();
    return false;
  }
});

const store = createStore(rootReducer);

// 创建 React Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 缓存时间：10分钟
      cacheTime: 10 * 60 * 1000,
      // 数据过期时间：5分钟
      staleTime: 5 * 60 * 1000,
      // 不在窗口聚焦时自动重新获取
      refetchOnWindowFocus: false,
      // 失败时重试1次
      retry: 1,
    },
  },
});

// 初始化主题色 CSS 变量
function initThemeColor() {
  const theme =
    document.querySelector('body')?.getAttribute('arco-theme') || 'light';
  const themeColor = defaultSettings.themeColor || '#0baf2d';
  const list = generate(themeColor, {
    list: true,
    dark: theme === 'dark',
  });
  list.forEach((l, index) => {
    const rgbStr = getRgbStr(l);
    document.body.style.setProperty(`--arcoblue-${index + 1}`, rgbStr);
  });
}

// 在应用启动时初始化主题色
initThemeColor();

function Index() {
  const [theme, setTheme] = useStorage('arco-theme', 'light');

  function getArcoLocale() {
    // 固定使用中文
    return zhCN;
  }

  // API 响应类型
  interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
  }

  interface UserProfile {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    department?: string;
    departmentId?: string;
    roles?: string[];
    position?: string;
  }

  const fetchUserInfo = useCallback(async (): Promise<void> => {
    store.dispatch({
      type: 'update-userInfo',
      payload: { userLoading: true },
    });

    try {
      // 并行获取用户信息和预取权限数据（复用 React Query 缓存）
      const [profileRes] = await Promise.all([
        apiClient.get<ApiResponse<UserProfile>>('/user/profile'),
        // 使用 queryClient 预取原始权限，与 usePermissions/useRawPermissions 共享同一个 queryKey
        queryClient.prefetchQuery({
          queryKey: ['permissions', 'raw'],
          queryFn: async () => {
            const result = await getUserPermissions();
            return result.permissions;
          },
          staleTime: 5 * 60 * 1000,
        }),
      ]);

      const profileData = profileRes as unknown as ApiResponse<UserProfile>;

      if (!profileData.success || !profileData.data) {
        store.dispatch({
          type: 'update-userInfo',
          payload: { userLoading: false },
        });
        return;
      }

      const userData = profileData.data;

      store.dispatch({
        type: 'update-userInfo',
        payload: {
          userInfo: {
            id: userData.id,
            name: userData.name,
            email: userData.email,
            avatar: userData.avatar,
            department: userData.department,
            departmentId: userData.departmentId,
            roles: userData.roles,
            job: userData.position,
            permissions: {}, // 权限由 usePermissions hook 统一管理
          },
          userLoading: false,
        },
      });
    } catch (error) {
      logger.error('获取用户信息失败', { error });
      store.dispatch({
        type: 'update-userInfo',
        payload: { userLoading: false },
      });
    }
  }, []);

  useEffect(() => {
    // 如果在登录页，不需要检查登录状态和获取用户信息
    const isLoginPage = window.location.pathname.replace(/\//g, '') === 'login';
    if (isLoginPage) {
      return;
    }

    // 异步检查登录状态
    checkLogin().then((isLoggedIn) => {
      if (isLoggedIn) {
        fetchUserInfo();
      } else {
        window.location.pathname = '/login';
      }
    });
  }, [fetchUserInfo]);

  useEffect(() => {
    changeTheme(theme);
  }, [theme]);

  const contextValue = {
    theme,
    setTheme,
  };

  return (
    <BrowserRouter>
      <ConfigProvider
        locale={getArcoLocale()}
        componentConfig={{
          Card: {
            bordered: false,
          },
          List: {
            bordered: false,
          },
          Table: {
            border: false,
          },
        }}
      >
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <ProjectPermissionsProvider>
              <GlobalContext.Provider value={contextValue}>
                <Switch>
                  <Route path="/login" component={Login} />
                  <Route path="/" component={PageLayout} />
                </Switch>
              </GlobalContext.Provider>
            </ProjectPermissionsProvider>
          </Provider>
          {/* React Query DevTools - 已禁用 */}
          {/* {process.env.NODE_ENV === 'development' && (
            <ReactQueryDevtools initialIsOpen={false} />
          )} */}
        </QueryClientProvider>
      </ConfigProvider>
    </BrowserRouter>
  );
}

ReactDOM.render(<Index />, document.getElementById('root'));
